import type { RouteRecord } from 'vite-react-ssg';
import { DEFAULT_LOCALE, LOCALES, PAGES, routeSlugFor, type Locale } from '@nexa/content';
import { RootLayout } from '@/core/layout';
import { localeFromSearch } from '@/core/i18n';
import NotFoundPage from '@/pages/NotFoundPage.tsx';
import { PAGE_MODULE } from './pageModules.ts';

/**
 * CÂY ROUTE ĐƯỢC SINH RA, KHÔNG VIẾT TAY.
 *
 * Sinh từ `PAGES` thì router, sitemap và hreflang không thể lệch: thêm một dòng vào bảng route
 * là có ngay URL cho mọi ngôn ngữ, có mục trong sitemap, có thẻ hreflang, và có mặt trong menu.
 *
 * Hình dạng URL: `/<slug>/` cho ngôn ngữ mặc định, `/<slug>/?lang=<locale>` cho ngôn ngữ khác —
 * locale nằm ở QUERY, không còn ở prefix path (user chốt 2026-08-30, xem
 * `docs/design/2026-08-27-lang-query-param-design.md`). Hệ quả với cây route: nó KHÔNG còn biết
 * tới locale nữa. `/terms/` là một route duy nhất; ngôn ngữ do `?lang` quyết định.
 */
function pageChildren(locale: Locale): RouteRecord[] {
  const pageRoutes: RouteRecord[] = PAGES.map((page) => {
    const load = PAGE_MODULE[page.id][locale];
    const lazy = async () => ({ Component: (await load()).default });
    const slug = routeSlugFor(page.id, locale);

    return slug === ''
      ? { index: true, lazy, handle: { pageId: page.id } }
      : { path: slug, lazy, handle: { pageId: page.id } };
  });

  // Catch-all: gõ sai URL vẫn thấy trang 404 ĐÚNG NGÔN NGỮ, còn nguyên header/footer để đi tiếp.
  // Route động nên vite-react-ssg tự bỏ qua khi prerender.
  return [...pageRoutes, { path: '*', Component: NotFoundPage }];
}

/**
 * Locale của bản đang chạy, chốt MỘT LẦN ở module scope.
 *
 * Trên trình duyệt: đọc `?lang` của URL hiện tại — cùng giá trị nginx dùng để chọn file, nên cây
 * React và HTML tĩnh không thể lệch. Đọc ở module scope là ĐÚNG thời điểm: nó chạy khi bundle
 * được nạp, tức URL đã có, và trước khi route `lazy` nào kịp chọn module.
 *
 * Lúc prerender (Node, không có `window`): ra `DEFAULT_LOCALE` — đúng bản sẽ nằm ở gốc `dist/`.
 *
 * Hằng, không phải state: đổi ngôn ngữ là TẢI LẠI trang (`LanguageSwitcher` dùng `<a>`), vì
 * `?lang` đổi mà path không đổi thì router coi là cùng route và sẽ không remount.
 */
const SERVED_LOCALE = localeFromSearch();

export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootLayout locale={SERVED_LOCALE} />,
    entry: 'src/core/layout/RootLayout.tsx',
    children: pageChildren(SERVED_LOCALE),
  },

  /*
   * Nhánh CHỈ ĐỂ PRERENDER. Người dùng không bao giờ tới đây: nginx khai `location ^~ /__lang/`
   * là `internal`, nên request trực tiếp trả 404. Nó tồn tại vì vite-react-ssg sinh file theo
   * PATH của route, nên muốn có bản HTML thứ hai thì phải có path thứ hai.
   *
   * Sinh TĨNH từ `LOCALES`, KHÔNG dùng route động `/__lang/:locale`: vite-react-ssg bỏ qua route
   * động khi prerender — đúng cơ chế khiến `path: '*'` ở trên an toàn — nên route động sẽ không
   * sinh ra file nào, và chỗ phát hiện sẽ là cổng `check:seo`, sau khi đã build xong.
   */
  ...LOCALES.filter((locale) => locale !== DEFAULT_LOCALE).map<RouteRecord>((locale) => ({
    path: `/__lang/${locale}`,
    element: <RootLayout locale={locale} />,
    entry: 'src/core/layout/RootLayout.tsx',
    children: pageChildren(locale),
  })),

  // Trang 404 TĨNH để nginx trả về (`error_page 404 /404/index.html`). Không có nó thì URL sai
  // sẽ nhận HTML của trang chủ kèm mã 200 — Google coi đó là "soft 404" và trừ điểm cả site.
  {
    path: '/404',
    element: <RootLayout locale={DEFAULT_LOCALE} />,
    entry: 'src/core/layout/RootLayout.tsx',
    children: [{ index: true, Component: NotFoundPage }],
  },
];
